import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCircle, Upload, Bell, CheckCircle2, Clock, XCircle, Vote } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const statusIcon = { verified: CheckCircle2, pending: Clock, rejected: XCircle };
const statusColor = { verified: "text-emerald-600", pending: "text-amber-600", rejected: "text-destructive" };

export default function Profile() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [votes, setVotes] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [prefs, setPrefs] = useState({ new_question: true, closing_reminder: true });
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (!user) return;
    setPrefs(user.notif_prefs || { new_question: true, closing_reminder: true });
    api.get("/me/votes").then(({ data }) => setVotes(data));
    api.get("/notifications").then(({ data }) => setNotifs(data));
  }, [user, nav]);

  if (!user) return <div className="max-w-4xl mx-auto px-4 py-12 text-muted-foreground">{t("common.loading")}</div>;

  const Icon = statusIcon[user.verification_status] || Clock;

  const upload = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/auth/upload-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("profile.docUploaded"));
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const savePrefs = async (next) => {
    setPrefs(next);
    try { await api.put("/notifications/prefs", next); toast.success("OK"); } catch { toast.error("Error"); }
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setNotifs(notifs.map((n) => ({ ...n, read: true })));
  };

  const notifLabel = (n) => {
    if (n.type === "new_question") return `${t("notif.new_question")}: ${n.title}`;
    if (n.type === "verification") return t(`notif.${n.title}`);
    return n.title;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-8">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center"><UserCircle className="h-9 w-9 text-primary" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email} · {user.country} · {user.role}</p>
        </div>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("profile.status")}</div>
            <div data-testid="verify-status" className={`flex items-center gap-2 font-heading font-bold text-lg mt-1 ${statusColor[user.verification_status]}`}><Icon className="h-5 w-5" />{t(`status.${user.verification_status}`)}</div>
          </div>
          {user.role === "citizen" && (
            <div className="flex items-center gap-2">
              <Input data-testid="doc-file" type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0])} className="max-w-xs" />
              <Button data-testid="doc-upload-btn" onClick={upload} disabled={!file} variant="outline"><Upload className="h-4 w-4 mr-1" />{t("profile.uploadDoc")}</Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2 mb-4"><Bell className="h-5 w-5 text-primary" />{t("profile.notifPrefs")}</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between"><Label htmlFor="np1">{t("profile.notifNew")}</Label><Switch id="np1" data-testid="pref-new" checked={prefs.new_question} onCheckedChange={(v) => savePrefs({ ...prefs, new_question: v })} /></div>
          <div className="flex items-center justify-between"><Label htmlFor="np2">{t("profile.notifClose")}</Label><Switch id="np2" data-testid="pref-close" checked={prefs.closing_reminder} onCheckedChange={(v) => savePrefs({ ...prefs, closing_reminder: v })} /></div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Bell className="h-5 w-5 text-primary" />{t("profile.notifications")}</h2>
          {notifs.some((n) => !n.read) && <Button data-testid="mark-all-read" onClick={markAll} variant="ghost" size="sm">{t("profile.markAllRead")}</Button>}
        </div>
        {notifs.length === 0 ? <p className="text-sm text-muted-foreground">{t("profile.noNotif")}</p> : (
          <ul className="space-y-2">
            {notifs.map((n) => (
              <li key={n.id} data-testid={`notif-${n.id}`} className={`text-sm rounded-md p-3 border ${n.read ? "border-border bg-transparent text-muted-foreground" : "border-primary/20 bg-primary/5 font-medium"}`}>
                {n.question_id ? <Link to={`/question/${n.question_id}`} className="hover:underline">{notifLabel(n)}</Link> : notifLabel(n)}
                <div className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2 mb-4"><Vote className="h-5 w-5 text-primary" />{t("profile.history")}</h2>
        {votes.length === 0 ? <p className="text-sm text-muted-foreground">{t("profile.noVotes")}</p> : (
          <ul className="space-y-2">
            {votes.map((v) => (
              <li key={v.hash} data-testid={`vote-history-${v.index}`} className="rounded-md border border-border p-3">
                <Link to={`/question/${v.question_id}`} className="font-medium hover:underline">{v.question_title}</Link>
                <div className="text-sm text-primary mt-1">→ {v.option_label}</div>
                <div className="text-[11px] font-mono text-muted-foreground mt-1 break-all">#{v.index} · {v.hash.slice(0, 32)}…</div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
